import { useEffect, useRef } from "react";
import { Controller, type Control, type FieldPathByValue, type FieldValues as FieldValues$ } from "react-hook-form";

import TextField from "@/components/TextField";

import type { CareerPlanDocumentFormValues } from "./careerPlanDocument";

type StringFieldName<FieldValues extends FieldValues$> = FieldPathByValue<FieldValues, string>;

export function focusPlanTextField(field: HTMLElement | null): () => void {
  if (!field) return () => {};

  let cancelled = false;
  let frame = 0;
  const litField = field as HTMLElement & { updateComplete?: Promise<unknown> };

  const focusField = () => {
    if (cancelled) return;
    field.focus();
    field.shadowRoot?.querySelector<HTMLElement>("input, textarea")?.focus();
  };

  if (litField.updateComplete) {
    void litField.updateComplete.then(() => {
      if (!cancelled) frame = requestAnimationFrame(focusField);
    });
  } else {
    focusField();
  }

  return () => {
    cancelled = true;
    cancelAnimationFrame(frame);
  };
}

export default function AutoFocusedPlanField<
  FieldValues extends FieldValues$ = CareerPlanDocumentFormValues,
  Name extends StringFieldName<FieldValues> = StringFieldName<FieldValues>
>({
  control,
  name,
  label,
  type,
  rows,
  autoFocus = true,
  disabled = false
}: {
  control: Control<FieldValues>;
  name: Name;
  label: string;
  type?: "textarea";
  rows?: number;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!autoFocus) return;
    return focusPlanTextField(inputRef.current);
  }, [autoFocus]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <TextField
          {...field}
          value={field.value ?? ""}
          label={label}
          type={type}
          rows={rows}
          className="w-full"
          disabled={disabled}
          ref={(element) => {
            field.ref(element);
            inputRef.current = element;
          }}
        />
      )}
    />
  );
}
